import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { warn } from "@/lib/log";
import { compressImage } from "@/lib/imageCompress";
import { shouldRetryLater, uploadBlob } from "@/lib/files";
import useOfflinePhotoQueue from "@/hooks/useOfflinePhotoQueue";
import { addSectionFile, removeSectionFile, retagPhoto } from "@/lib/visitSchema";

const uploadFile = async (blob, section, filename, mime, onProgress) => (await uploadBlob(blob, { section, filename, mime, onProgress })).file_id;

/* Compress + upload one capture. Returns "uploaded" | "queue" (transient failure) | "rejected". */
async function processCapture(raw, online, upload, onError) {
  let file;
  try { file = await compressImage(raw); } catch (err) { onError(`${raw.name}: ${err.message}`); return { kind: "rejected" }; }
  if (!online) return { kind: "queue", file };
  try {
    return { kind: "uploaded", file, fileId: await upload(file) };
  } catch (err) {
    if (shouldRetryLater(err)) { warn("upload failed, queuing offline", err?.message || err); return { kind: "queue", file }; }
    onError(`${raw.name}: ${formatApiError(err)}`);
    return { kind: "rejected" };
  }
}

function announceCapture({ uploaded, queued, errors, online }) {
  if (uploaded) toast.success(`${uploaded} photo(s) uploaded`);
  if (queued) toast.warning(online ? `${queued} photo(s) could not reach the server and were saved on this device — tap “Sync now” to retry.` : `${queued} photo(s) saved offline — they sync automatically when you are back online.`);
  errors.forEach((m) => toast.error(m, { duration: 9000 }));
}

/* Section-aware photo capture with an offline queue. Attaches uploaded file ids into the form via setForm. */
export default function useVisitUploads({ leadId, online, setForm }) {
  const queue = useOfflinePhotoQueue(leadId);
  const [uploading, setUploading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [progress, setProgress] = useState(null); // { section, index, total, pct, stage }

  const attach = useCallback((section, fileId, tag) => setForm((prev) => addSectionFile(prev, section, fileId, tag)), [setForm]);

  const capture = useCallback(async (section, files, tag = "other") => {
    if (!files.length) return;
    setUploading(true);
    const counts = { uploaded: 0, queued: 0, errors: [], online };
    try {
      for (let i = 0; i < files.length; i += 1) {
        const step = (stage, pct = 0) => setProgress({ section, index: i + 1, total: files.length, pct, stage });
        step("compressing");
        const upload = (f) => { step("uploading"); return uploadFile(f, section, f.name, f.type, (pct) => step("uploading", pct)); };
        const result = await processCapture(files[i], online, upload, (m) => counts.errors.push(m));
        if (result.kind === "uploaded") { attach(section, result.fileId, tag); counts.uploaded += 1; }
        if (result.kind === "queue") { await queue.enqueue(section, result.file, tag); counts.queued += 1; }
      }
      announceCapture(counts);
    } finally { setUploading(false); setProgress(null); }
  }, [attach, online, queue]);

  const syncPending = useCallback(async () => {
    const attached = [];
    if (!queue.pendingRef.current.length || syncing) return attached;
    setSyncing(true);
    const dropped = [];
    let stalled = false;
    try {
      const items = [...queue.pendingRef.current];
      for (let i = 0; i < items.length && !stalled; i += 1) {
        const p = items[i];
        setProgress({ section: p.section, index: i + 1, total: items.length, pct: 0, stage: "syncing" });
        try {
          const fileId = await uploadFile(p.blob, p.section, p.filename, p.mime, (pct) => setProgress((cur) => cur && { ...cur, pct }));
          attach(p.section, fileId, p.tag);
          attached.push({ section: p.section, file_id: fileId, tag: p.tag });
          await queue.drop(p.local_id);
        } catch (err) {
          if (shouldRetryLater(err)) { stalled = true; break; }
          dropped.push(`${p.filename}: ${formatApiError(err)}`);
          await queue.drop(p.local_id);
        }
      }
      if (attached.length) toast.success(`Synced ${attached.length} photo(s)`);
      dropped.forEach((m) => toast.error(`Removed from queue — ${m}`, { duration: 9000 }));
      if (stalled) toast.warning("Could not sync the remaining photo(s) — retry when the connection is better.");
    } finally { setSyncing(false); setProgress(null); }
    return attached;
  }, [attach, queue, syncing]);

  const wasOnline = useRef(online);
  useEffect(() => {
    const cameOnline = online && !wasOnline.current;
    wasOnline.current = online;
    if (cameOnline && queue.pendingRef.current.length) syncPending().catch(() => {});
  }, [online, queue, syncPending]);

  const removeFile = useCallback((section, fileId) => setForm((prev) => removeSectionFile(prev, section, fileId)), [setForm]);
  const retag = useCallback((fileId, tag) => setForm((prev) => retagPhoto(prev, fileId, tag)), [setForm]);

  return { pending: queue.pending, pendingFor: queue.pendingFor, uploading, syncing, progress, capture, syncPending, removePending: queue.drop, removeFile, retag };
}
